import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mobile-services safety acknowledgement.
 *
 * Post Phase 3 cleanup: there is no manual ID review, no admin approval
 * queue, and no separate government-ID upload here. Stripe Connect KYC is
 * the sole identity verification, and mobile services activate automatically
 * once Stripe confirms the business AND the safety policy is acknowledged
 * (a one-tap agreement below).
 */
export default function MobileVerification() {
  const { user, authHeader, refreshUser } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [ackChecked, setAckChecked] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mobile-verification`, { headers: authHeader() });
      setState(r.data);
      setAckChecked(!!r.data.safety_policy_acknowledged);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);

  const ackPolicy = async () => {
    setBusy("policy");
    try {
      await axios.post(`${API}/mobile-verification/acknowledge-safety`, {}, { headers: authHeader() });
      toast.success("Safety policy acknowledged");
      await load();
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy("");
    }
  };

  if (loading || !state) {
    return (
      <div className="App"><Header /><div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-sm" style={{ color: "var(--gf-text-muted)" }}>Loading…</div><Footer /></div>
    );
  }

  const acknowledged = !!state.safety_policy_acknowledged;
  const stripeVerified = !!user?.stripe_charges_enabled;
  const isMobileReady = !!user?.mobile_verified;

  const pill = isMobileReady
    ? { label: "Ready for mobile services", color: "var(--gf-success)" }
    : acknowledged
      ? { label: "Awaiting Stripe verification", color: "var(--gf-warning)" }
      : { label: "Action required", color: "var(--gf-text-muted)" };

  return (
    <div className="App">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-xs font-medium" style={{ color: "var(--gf-text-soft)" }}>BUSINESS · SAFETY</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight" style={{ color: "var(--gf-text)" }}>
          Mobile services safety
        </h1>
        <p className="mt-3 text-base" style={{ color: "var(--gf-text-soft)" }}>
          A one-time acknowledgement before you can offer mobile, at-customer-location, or
          in-home services. Identity verification is handled automatically during payout setup —
          BookFreely does not collect or review a separate government-issued ID.
        </p>

        <div data-testid="mv-status-pill" className="mt-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs"
             style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: pill.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: pill.color }} />
          {pill.label}
        </div>

        <div className="mt-8 space-y-4">
          {/* Step 1: Stripe Connect KYC — read-only status */}
          <div data-testid="mv-step-stripe" className="rounded-2xl border p-5 sm:p-6"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full grid place-items-center text-xs font-medium border"
                     style={{
                       borderColor: stripeVerified ? "var(--gf-success)" : "var(--gf-border-strong)",
                       color: stripeVerified ? "var(--gf-success)" : "var(--gf-text)",
                       background: stripeVerified ? "var(--gf-surface)" : "transparent",
                     }}>{stripeVerified ? "✓" : "1"}</div>
                <h3 className="font-display text-lg sm:text-xl" style={{ color: "var(--gf-text)" }}>Identity verification</h3>
              </div>
              {stripeVerified && <span className="text-xs" style={{ color: "var(--gf-success)" }}>Complete</span>}
            </div>
            <div className="ml-10 mt-2 text-sm" style={{ color: "var(--gf-text-soft)" }}>
              {stripeVerified ? (
                <p>Your identity and business details are verified. You&rsquo;re good on this step.</p>
              ) : (
                <p>Finish payout setup from your business dashboard. Once your identity is confirmed, this step turns green automatically.</p>
              )}
            </div>
          </div>

          {/* Step 2: Safety policy acknowledgement */}
          <div data-testid="mv-step-policy" className="rounded-2xl border p-5 sm:p-6"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full grid place-items-center text-xs font-medium border"
                     style={{
                       borderColor: acknowledged ? "var(--gf-success)" : "var(--gf-border-strong)",
                       color: acknowledged ? "var(--gf-success)" : "var(--gf-text)",
                       background: acknowledged ? "var(--gf-surface)" : "transparent",
                     }}>{acknowledged ? "✓" : "2"}</div>
                <h3 className="font-display text-lg sm:text-xl" style={{ color: "var(--gf-text)" }}>Safety policy acknowledgement</h3>
              </div>
              {acknowledged && <span className="text-xs" style={{ color: "var(--gf-success)" }}>Complete</span>}
            </div>
            <div className="ml-10 mt-3 text-sm space-y-2" style={{ color: "var(--gf-text-soft)" }}>
              <p>By providing mobile / at-customer-location services through BookFreely, I confirm:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>I (or my authorised team member) will arrive in person, and the person delivering the service is the one identified in my Stripe-verified business account.</li>
                <li>I will follow all <Link to="/help" className="underline" style={{ color: "var(--gf-text)" }}>BookFreely safety rules</Link> — no harassment, no discrimination, no fraudulent or scam activity.</li>
                <li>I will keep all customer information strictly confidential and never share it outside the booking.</li>
                <li>If a different team member will be attending, I will notify the customer through the BookFreely platform before arrival.</li>
                <li>BookFreely may suspend my mobile-services capability if I violate these policies.</li>
              </ul>
              {!acknowledged && (
                <>
                  <label className="mt-3 flex items-center gap-2 text-sm" style={{ color: "var(--gf-text)" }}>
                    <input data-testid="mv-policy-check" type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} />
                    I have read and agree to the BookFreely safety policies.
                  </label>
                  <button data-testid="mv-policy-confirm-btn" onClick={ackPolicy} disabled={!ackChecked || busy === "policy"}
                          className="mt-3 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
                          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                    {busy === "policy" ? "Saving…" : "Confirm"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {isMobileReady && (
          <div data-testid="mv-ready-banner" className="mt-8 rounded-2xl border p-5 text-sm"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <b style={{ color: "var(--gf-success)" }}>You&rsquo;re ready.</b> Your business can offer mobile / in-home services. Go to your dashboard and create a service with location type &ldquo;Mobile&rdquo; or &ldquo;In customer&rsquo;s home&rdquo;.
            <div className="mt-3">
              <button onClick={() => nav("/business")} className="rounded-md px-4 py-2 text-sm font-medium"
                      style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                Go to dashboard
              </button>
            </div>
          </div>
        )}

        {!isMobileReady && acknowledged && !stripeVerified && (
          <div data-testid="mv-awaiting-stripe-banner" className="mt-8 rounded-2xl border p-5 text-sm"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-soft)" }}>
            <b style={{ color: "var(--gf-text)" }}>Safety policy saved.</b> Mobile services will unlock automatically once your identity is confirmed during payout setup.
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
